import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import type { ReactNode } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  AlertCircle,
  CheckCircle2,
  Info,
  AlertTriangle,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";

export type AlertType = "success" | "error" | "warning" | "info";

export interface Alert {
  id: string;
  type: AlertType;
  title?: string;
  message: string;
  duration?: number;
  description?: string;
}

// Componente de Toast discreto para success
const SuccessToast = ({
  alerts,
  onDismiss,
}: {
  alerts: Alert[];
  onDismiss: (id: string) => void;
}) => {
  useEffect(() => {
    alerts.forEach((alert) => {
      const timer = setTimeout(() => {
        onDismiss(alert.id);
      }, alert.duration || 3000);
      return () => clearTimeout(timer);
    });
  }, [alerts, onDismiss]);

  if (alerts.length === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 z-[100] flex flex-col-reverse gap-2 max-w-sm">
      {alerts.map((alert) => (
        <div
          key={alert.id}
          className="flex items-center gap-3 p-3 rounded-lg border bg-green-50 border-green-200 dark:bg-green-950/90 dark:border-green-800 shadow-lg animate-in slide-in-from-right-5 fade-in duration-300"
        >
          <CheckCircle2 className="h-5 w-5 text-green-500 shrink-0" />
          <div className="flex-1 min-w-0">
            {alert.title && (
              <p className="font-medium text-sm text-green-800 dark:text-green-200">
                {alert.title}
              </p>
            )}
            <p className="text-sm text-green-700 dark:text-green-300">
              {alert.message}
            </p>
          </div>
          <button
            onClick={() => onDismiss(alert.id)}
            className="shrink-0 p-1 rounded hover:bg-green-100 dark:hover:bg-green-800/50 transition-colors"
          >
            <X className="h-4 w-4 text-green-600 dark:text-green-400" />
          </button>
        </div>
      ))}
    </div>
  );
};

interface AlertModalContextType {
  alerts: Alert[];
  showAlert: (
    type: AlertType,
    message: string,
    options?: { title?: string; description?: string; duration?: number },
  ) => void;
  success: (
    message: string,
    options?: { title?: string; description?: string; duration?: number },
  ) => void;
  error: (
    message: string,
    options?: { title?: string; description?: string; duration?: number },
  ) => void;
  warning: (
    message: string,
    options?: { title?: string; description?: string; duration?: number },
  ) => void;
  info: (
    message: string,
    options?: { title?: string; description?: string; duration?: number },
  ) => void;
  dismissAlert: (id: string) => void;
  dismissAll: () => void;
}

const AlertModalContext = createContext<AlertModalContextType | undefined>(
  undefined,
);

const alertConfig: Record<
  AlertType,
  { icon: React.ElementType; className: string; defaultTitle: string }
> = {
  success: {
    icon: CheckCircle2,
    className: "text-green-500",
    defaultTitle: "Sucesso",
  },
  error: {
    icon: AlertCircle,
    className: "text-red-500",
    defaultTitle: "Erro",
  },
  warning: {
    icon: AlertTriangle,
    className: "text-yellow-500",
    defaultTitle: "Atenção",
  },
  info: {
    icon: Info,
    className: "text-blue-500",
    defaultTitle: "Informação",
  },
};

export const AlertModalProvider = ({ children }: { children: ReactNode }) => {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [successToasts, setSuccessToasts] = useState<Alert[]>([]);
  const [isOpen, setIsOpen] = useState(false);

  const generateId = () => Math.random().toString(36).substring(2, 9);

  const showAlert = useCallback(
    (
      type: AlertType,
      message: string,
      options?: { title?: string; description?: string; duration?: number },
    ) => {
      const newAlert: Alert = {
        id: generateId(),
        type,
        message,
        title: options?.title,
        description: options?.description,
        duration: options?.duration,
      };

      // Success vai para toasts discretos
      if (type === "success") {
        setSuccessToasts((prev) => [...prev, newAlert]);
      } else {
        setAlerts((prev) => [...prev, newAlert]);
        setIsOpen(true);
      }
    },
    [],
  );

  const success = useCallback(
    (
      message: string,
      options?: { title?: string; description?: string; duration?: number },
    ) => {
      showAlert("success", message, options);
    },
    [showAlert],
  );

  const dismissSuccessToast = useCallback((id: string) => {
    setSuccessToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const error = useCallback(
    (
      message: string,
      options?: { title?: string; description?: string; duration?: number },
    ) => {
      showAlert("error", message, options);
    },
    [showAlert],
  );

  const warning = useCallback(
    (
      message: string,
      options?: { title?: string; description?: string; duration?: number },
    ) => {
      showAlert("warning", message, options);
    },
    [showAlert],
  );

  const info = useCallback(
    (
      message: string,
      options?: { title?: string; description?: string; duration?: number },
    ) => {
      showAlert("info", message, options);
    },
    [showAlert],
  );

  const dismissAlert = useCallback((id: string) => {
    setAlerts((prev) => {
      const newAlerts = prev.filter((alert) => alert.id !== id);
      if (newAlerts.length === 0) {
        setIsOpen(false);
      }
      return newAlerts;
    });
  }, []);

  const dismissAll = useCallback(() => {
    setAlerts([]);
    setIsOpen(false);
  }, []);

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      dismissAll();
    }
  };

  return (
    <AlertModalContext.Provider
      value={{
        alerts,
        showAlert,
        success,
        error,
        warning,
        info,
        dismissAlert,
        dismissAll,
      }}
    >
      {children}
      {/* Toast discreto para success */}
      <SuccessToast alerts={successToasts} onDismiss={dismissSuccessToast} />

      {/* Modal centralizado para error/warning/info */}
      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-md" showCloseButton={false}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {alerts[0] &&
                React.createElement(alertConfig[alerts[0].type].icon, {
                  className: cn(
                    "h-5 w-5",
                    alertConfig[alerts[0].type].className,
                  ),
                })}
              {alerts.length > 1
                ? `${alerts.length} Alertas`
                : alerts[0]?.title ||
                  alertConfig[alerts[0]?.type || "error"].defaultTitle}
            </DialogTitle>
            {alerts.length === 1 && alerts[0]?.description && (
              <DialogDescription>{alerts[0].description}</DialogDescription>
            )}
          </DialogHeader>

          <ScrollArea className={cn(alerts.length > 3 ? "h-64" : "")}>
            <div className="space-y-3 pr-4">
              {alerts.map((alert) => {
                const config = alertConfig[alert.type];
                const Icon = config.icon;

                return (
                  <div
                    key={alert.id}
                    className={cn(
                      "flex items-start gap-3 p-3 rounded-lg border",
                      alert.type === "error" &&
                        "bg-red-50 border-red-200 dark:bg-red-950/20 dark:border-red-800",
                      alert.type === "warning" &&
                        "bg-yellow-50 border-yellow-200 dark:bg-yellow-950/20 dark:border-yellow-800",
                      alert.type === "info" &&
                        "bg-blue-50 border-blue-200 dark:bg-blue-950/20 dark:border-blue-800",
                    )}
                  >
                    <Icon
                      className={cn(
                        "h-5 w-5 mt-0.5 shrink-0",
                        config.className,
                      )}
                    />
                    <div className="flex-1 min-w-0">
                      {alerts.length > 1 && (
                        <p className="font-medium text-sm">
                          {alert.title || config.defaultTitle}
                        </p>
                      )}
                      <p className="text-sm text-foreground/80">
                        {alert.message}
                      </p>
                      {alert.description && alerts.length > 1 && (
                        <p className="text-xs text-muted-foreground mt-1">
                          {alert.description}
                        </p>
                      )}
                    </div>
                    {alerts.length > 1 && (
                      <button
                        onClick={() => dismissAlert(alert.id)}
                        className="shrink-0 p-1 rounded hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
                      >
                        <X className="h-4 w-4 text-muted-foreground" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </ScrollArea>

          <DialogFooter>
            <Button onClick={dismissAll} className="w-full">
              {alerts.length > 1 ? "Fechar todos" : "OK"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AlertModalContext.Provider>
  );
};

export const useAlert = (): AlertModalContextType => {
  const context = useContext(AlertModalContext);
  if (!context) {
    throw new Error("useAlert must be used within an AlertModalProvider");
  }
  return context;
};

// Objeto global para usar fora de componentes React (compatibilidade com toast)
let globalAlertFunctions: AlertModalContextType | null = null;

export const setGlobalAlertFunctions = (functions: AlertModalContextType) => {
  globalAlertFunctions = functions;
};

export const alert = {
  success: (
    message: string,
    options?: { title?: string; description?: string; duration?: number },
  ) => {
    if (globalAlertFunctions) {
      globalAlertFunctions.success(message, options);
    } else {
      console.warn("AlertModalProvider not initialized");
    }
  },
  error: (
    message: string,
    options?: { title?: string; description?: string; duration?: number },
  ) => {
    if (globalAlertFunctions) {
      globalAlertFunctions.error(message, options);
    } else {
      console.warn("AlertModalProvider not initialized");
    }
  },
  warning: (
    message: string,
    options?: { title?: string; description?: string; duration?: number },
  ) => {
    if (globalAlertFunctions) {
      globalAlertFunctions.warning(message, options);
    } else {
      console.warn("AlertModalProvider not initialized");
    }
  },
  info: (
    message: string,
    options?: { title?: string; description?: string; duration?: number },
  ) => {
    if (globalAlertFunctions) {
      globalAlertFunctions.info(message, options);
    } else {
      console.warn("AlertModalProvider not initialized");
    }
  },
  dismissAll: () => {
    if (globalAlertFunctions) {
      globalAlertFunctions.dismissAll();
    }
  },
};
